import { useEffect, useRef, useState, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';
import {
  selectBundlePagesData,
  selectBundleRetrieveActivePageIndex,
  selectBundleRetrievePageScale,
  selectBundleRetrievePageIds,
  selectSharedDataActiveBundleType,
  setBundleRetrieveActivePageIndex,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { debounce } from '@/utils';
import { BundleItemDataExtended } from '@/api';
import { BasePdfSinglePageRenderer } from '@repo/atomic-components/pdf';

const ITEM_FOOTER_HEIGHT = 24;
const IGNORE_SCROLL_DELAY_MS = 500;
const PAGE_MANUAL_SCROLL_DELAY_MS = 500;

export const RetrievePagesDialogVirtualizedList = () => {
  const dispatch = useAppDispatch();

  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const listIndexRef = useRef<number>(0);
  const isIgnoreScroll = useRef<boolean>(false);

  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundlePagesData = useAppSelector(selectBundlePagesData);

  const bundleRetrievePageIds = useAppSelector(selectBundleRetrievePageIds);
  const bundleRetrieveActivePageIndex = useAppSelector(selectBundleRetrieveActivePageIndex);
  const bundleRetrievePageScale = useAppSelector(selectBundleRetrievePageScale);

  const [containerHeight, setContainerHeight] = useState<number>(0);

  const retrievePagesData = useMemo(() => {
    if (!Array.isArray(bundleRetrievePageIds) || !bundlePagesData) return [];

    return bundleRetrievePageIds.reduce<BundleItemDataExtended[]>((acc, pageId) => {
      const pageData = bundlePagesData.get(pageId);
      if (pageData) {
        acc.push(pageData);
      } else {
        console.warn(`Page data for pageId ${pageId} not found in bundlePagesData`);
      }
      return acc;
    }, []);
  }, [bundleRetrievePageIds, bundlePagesData]);

  const { maxWidth } = useMemo(() => {
    const widths = retrievePagesData.map((pageId) => pageId.pageDimensions.width * bundleRetrievePageScale);
    const maxWidth = Math.max(...widths);
    return { maxWidth };
  }, [bundleRetrievePageScale, retrievePagesData]);

  const virtualizer = useVirtualizer({
    count: retrievePagesData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index: number) =>
      retrievePagesData[index].pageDimensions.height * bundleRetrievePageScale + ITEM_FOOTER_HEIGHT,
    horizontal: false,
    overscan: 5,
  });

  const items: VirtualItem[] = virtualizer.getVirtualItems();

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!parentRef.current) return;

      if (isIgnoreScroll.current) {
        setTimeout(() => {
          isIgnoreScroll.current = false;
        }, IGNORE_SCROLL_DELAY_MS);
      }

      const containerRect = parentRef.current.getBoundingClientRect();
      const containerMidpoint = containerRect.top + containerRect.height / 2;

      items.forEach((virtualItem) => {
        const element = parentRef.current!.querySelector(`[data-index="${virtualItem.index}"]`) as HTMLElement | null;

        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= containerMidpoint && rect.top + rect.height >= containerMidpoint) {
            const newPage = virtualItem.index;
            if (activeBundleType && listIndexRef.current !== newPage) {
              listIndexRef.current = newPage;
              dispatch(
                setBundleRetrieveActivePageIndex({
                  key: activeBundleType,
                  data: newPage,
                }),
              );
            }
          }
        }
      });
    };

    const debouncedHandleScroll = debounce(handleScroll, PAGE_MANUAL_SCROLL_DELAY_MS);

    const scrollElement = parentRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', debouncedHandleScroll);
      handleScroll();
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', debouncedHandleScroll);
      }
    };
  }, [items, dispatch, containerHeight, activeBundleType]);

  useEffect(() => {
    if (bundleRetrieveActivePageIndex) {
      if (
        bundleRetrieveActivePageIndex >= 0 &&
        bundleRetrieveActivePageIndex < retrievePagesData.length &&
        listIndexRef.current !== bundleRetrieveActivePageIndex
      ) {
        console.log(`PDF list scroll scroll to index: ${bundleRetrieveActivePageIndex}`);
        isIgnoreScroll.current = true;
        virtualizer.scrollToIndex(bundleRetrieveActivePageIndex, {
          align: 'center',
          behavior: 'auto',
        });
        listIndexRef.current = bundleRetrieveActivePageIndex;
      } else {
        console.warn(`Wrong index: ${bundleRetrieveActivePageIndex}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleRetrieveActivePageIndex]);

  return (
    <div ref={containerRef} className={`bg-fletchers-gray-50 h-full w-full overflow-hidden`}>
      <div
        ref={parentRef}
        className="w-full overflow-x-auto overflow-y-auto"
        style={{ height: `${containerHeight}px` }}
      >
        <div
          className="relative flex flex-col"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            minWidth: `${maxWidth}px`,
          }}
        >
          <div
            className="absolute top-0 left-0 w-full"
            style={{
              transform: `translateY(${items[0]?.start ?? 0}px)`,
            }}
          >
            {items.map((virtualItem) => {
              const pageData = retrievePagesData[virtualItem.index];

              if (!pageData || !pageData.pageUri || !pageData.pageDimensions.width) {
                return <span>File data is in an incorrect format</span>;
              }

              const calculatedPdfPageWidth = pageData.pageDimensions.width * bundleRetrievePageScale;
              const calculatedPdfPageHeight = pageData.pageDimensions.height * bundleRetrievePageScale;
              return (
                <div
                  key={virtualItem.key}
                  className="mx-auto w-full flex-shrink-0"
                  style={{
                    width: `${calculatedPdfPageWidth}px`,
                    height: `${calculatedPdfPageHeight + ITEM_FOOTER_HEIGHT}px`,
                  }}
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                  data-testid={pageData.pageId}
                >
                  <div className="border-fletchers-gray-110 border">
                    <BasePdfSinglePageRenderer
                      pdfFile={pageData.pageUri}
                      width={calculatedPdfPageWidth}
                      height={calculatedPdfPageHeight}
                    />
                  </div>
                  <div
                    className="flex items-center justify-center"
                    style={{
                      minHeight: `${ITEM_FOOTER_HEIGHT}px`,
                      height: `${ITEM_FOOTER_HEIGHT}px`,
                      maxHeight: `${ITEM_FOOTER_HEIGHT}px`,
                    }}
                  >
                    <div className="text-[12px]">{pageData.originalPageNumber}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
